import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import { X, WarningCircle, UserPlus, ClockCounterClockwise, Users, Phone, CalendarBlank } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { DateTimeField } from "@/components/datetime";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatTime12 } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";

export default function AddWalkinDialog({ open, onClose }) {
  const { user } = useAuth();
  const isStaff = user?.role === "staff";

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [reason, setReason] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [bookingType, setBookingType] = useState("new");
  const [overrideReason, setOverrideReason] = useState("");
  const [selectedSessionId, setSelectedSessionId] = useState("");
  const [busy, setBusy] = useState(false);
  const [slots, setSlots] = useState(null);
  const [slotLoading, setSlotLoading] = useState(false);
  const [existingPatient, setExistingPatient] = useState(null);
  const [patientContext, setPatientContext] = useState(null);
  const [checkingPhone, setCheckingPhone] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedProfileId, setSelectedProfileId] = useState("self");
  const [familyFormOpen, setFamilyFormOpen] = useState(false);
  const [familyForm, setFamilyForm] = useState({ name: "", relationship: "Other", phone: "" });
  const [familyBusy, setFamilyBusy] = useState(false);
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [staffDoctors, setStaffDoctors] = useState([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState("");
  const phoneTimer = useRef(null);

  useEffect(() => {
    if (!open) return;
    setName(""); setPhone(""); setReason("");
    setDate(new Date().toISOString().slice(0, 10));
    setBookingType("new"); setOverrideReason(""); setSelectedSessionId("");
    setSlots(null); setBusy(false);
    setExistingPatient(null); setPatientContext(null); setSelectedProfileId("self");
    setShowDetails(false);
    setFamilyFormOpen(false); setFamilyForm({ name: "", relationship: "Other", phone: "" });
    setSelectedLocationId("");
    setStaffDoctors([]);
    setSelectedDoctorId("");

    if (isStaff) {
      api.get("/staff/me")
        .then(({ data }) => {
          const doctors = data.doctors || [];
          setStaffDoctors(doctors);
          if (doctors.length === 1) {
            setSelectedDoctorId(doctors[0].id);
          }
        })
        .catch(() => setStaffDoctors([]));
    } else {
      api.get("/doctors/me/locations")
        .then(({ data }) => {
          const active = (data.locations || []).filter((l) => l.active);
          setLocations(active);
          if (active.length === 1) setSelectedLocationId(active[0].id);
        })
        .catch(() => setLocations([]));
    }
  }, [open, isStaff]);

  useEffect(() => {
    if (!open || !isStaff || !selectedDoctorId) return;
    setSelectedLocationId("");
    setLocations([]);
    api.get(`/doctors/${selectedDoctorId}/locations`)
      .then(({ data }) => {
        const active = (data.locations || []).filter((l) => l.active);
        setLocations(active);
        if (active.length === 1) setSelectedLocationId(active[0].id);
      })
      .catch(() => setLocations([]));
  }, [open, isStaff, selectedDoctorId]);

  useEffect(() => {
    if (!date) { setSlots(null); return; }
    setSlotLoading(true);
    setSelectedSessionId("");
    const params = new URLSearchParams({ date });
    if (selectedLocationId) params.set("location_id", selectedLocationId);
    if (isStaff && selectedDoctorId) params.set("doctor_id", selectedDoctorId);
    api.get(`/appointments/slots?${params.toString()}`)
      .then(({ data }) => setSlots(data))
      .catch(() => setSlots(null))
      .finally(() => setSlotLoading(false));
  }, [date, selectedLocationId, isStaff, selectedDoctorId]);

  const handleLocationChange = (locId) => {
    setSelectedLocationId(locId);
    setSelectedSessionId("");
    setSlots(null);
  };

  const loadPatientContext = useCallback(async (rawPhone) => {
    const trimmed = rawPhone.trim();
    if (trimmed.length < 4) return;
    setCheckingPhone(true);
    try {
      const params = new URLSearchParams({ phone: trimmed });
      if (date) params.set("queue_date", date);
      if (isStaff && selectedDoctorId) params.set("doctor_id", selectedDoctorId);
      const { data } = await api.get(`/appointments/patient-context?${params.toString()}`);
      setPatientContext(data);
      setExistingPatient(data?.patient || null);
      setSelectedProfileId("self");
      if (data?.patient?.name) setName(data.patient.name);
      else if (data?.saved_contact?.name) setName(data.saved_contact.name);
      return data;
    } catch {
      setPatientContext(null);
      setExistingPatient(null);
      return null;
    } finally {
      setCheckingPhone(false);
    }
  }, [date, isStaff, selectedDoctorId]);

  const handlePhoneChange = (value) => {
    setPhone(value);
    setExistingPatient(null);
    setPatientContext(null);
    setSelectedProfileId("self");
    if (phoneTimer.current) clearTimeout(phoneTimer.current);
    const trimmed = value.trim();
    if (trimmed.length < 4) return;
    phoneTimer.current = setTimeout(() => loadPatientContext(trimmed), 500);
  };

  useEffect(() => {
    if (!open || !showDetails || !phone.trim() || phone.trim().length < 4) return;
    if (phoneTimer.current) clearTimeout(phoneTimer.current);
    phoneTimer.current = setTimeout(() => loadPatientContext(phone), 350);
    return () => phoneTimer.current && clearTimeout(phoneTimer.current);
  }, [open, showDetails, phone, date, selectedDoctorId, loadPatientContext]);

  const selectedDependent = (patientContext?.dependents || []).find((d) => d.id === selectedProfileId);
  const selectedProfileKey = selectedDependent?.id || existingPatient?.id || (patientContext ? "walk_in" : null);
  const selectedFollowUp = selectedProfileKey ? patientContext?.follow_up_eligibility?.[selectedProfileKey] : null;
  const recentCompletedForProfile = (patientContext?.recent_completed || []).filter((a) => !selectedProfileKey || a.patient_id === selectedProfileKey);
  const selectedProfileLabel = selectedDependent
    ? `${selectedDependent.name} (${selectedDependent.relationship})`
    : existingPatient?.name || name || "Self";

  useEffect(() => {
    if (!showDetails) return;
    setBookingType((current) => current === "doctor_override" ? current : (selectedFollowUp?.eligible ? "follow_up" : "new"));
  }, [showDetails, selectedProfileKey, selectedFollowUp?.eligible]);

  const addFamilyMember = async () => {
    if (!existingPatient?.id) { toast.error("Find a registered patient first."); return; }
    if (!familyForm.name.trim()) { toast.error("Family member name is required."); return; }
    setFamilyBusy(true);
    try {
      const { data } = await api.post(`/appointments/patient-context/${existingPatient.id}/dependents`, {
        name: familyForm.name.trim(),
        relationship: familyForm.relationship,
        phone: familyForm.phone.trim() || null,
      });
      toast.success("Family member added");
      setFamilyFormOpen(false);
      setFamilyForm({ name: "", relationship: "Other", phone: "" });
      await loadPatientContext(phone);
      setSelectedProfileId(data.id);
      setName(data.name);
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Could not add family member");
    } finally {
      setFamilyBusy(false);
    }
  };

  const submit = async () => {
    if (!name.trim()) { toast.error("Patient name is required."); return; }
    if (!phone.trim()) { toast.error("Phone number is required."); return; }
    if (locations.length > 1 && !selectedLocationId) {
      toast.error("Please select a clinic location.");
      return;
    }
    if (bookingType === "doctor_override" && !overrideReason.trim()) {
      toast.error("Override reason is required when using doctor override.");
      return;
    }
    if (bookingType === "follow_up" && !selectedFollowUp?.eligible) {
      toast.error(selectedFollowUp?.reason || "This patient is not eligible for follow-up.");
      return;
    }
    setBusy(true);
    try {
      const selectedName = selectedDependent?.name || name.trim();
      const payload = {
        patient_name: selectedName,
        patient_phone: phone.trim(),
        family_member_id: selectedDependent?.id || undefined,
        reason: reason.trim() || "Walk-in",
        queue_date: date || undefined,
        booking_type: bookingType,
        doctor_override: bookingType === "doctor_override",
        override_reason: bookingType === "doctor_override" ? overrideReason.trim() : undefined,
        queue_session_id: selectedSessionId || undefined,
        location_id: selectedLocationId || undefined,
        source_appointment_id: bookingType === "follow_up" ? selectedFollowUp?.source_appointment_id : undefined,
      };
      if (isStaff && selectedDoctorId) {
        payload.doctor_id = selectedDoctorId;
      }
      await api.post("/appointments/walkin", payload, { timeout: 30000 });
      toast.success("Walk-in patient booked!");
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Could not book walk-in");
    } finally {
      setBusy(false);
    }
  };

  const regularSlotsAvailable = slots?.slots_left > 0;
  const bookingScopeOpen = !!slots?.clinic_open && !slots?.is_blocked_date && !!slots?.is_working_day;
  const overrideSlotsAvailable = bookingScopeOpen && (slots?.override_slots_left ?? 0) > 0;
  const showOverrideOption = !regularSlotsAvailable && overrideSlotsAvailable;
  const activeWarnings = patientContext?.active_appointments || [];
  const canSubmit = !busy && (regularSlotsAvailable || overrideSlotsAvailable);

  const slotMessage = !slots?.clinic_open ? (slots?.message || "Clinic is closed on this date.") :
    slots?.is_blocked_date ? (slots?.message || "Doctor is unavailable on this date.") :
    !slots?.is_working_day ? "Doctor is not working on this date." :
    !slots?.available && regularSlotsAvailable ? `${slots.slots_left} regular slots available — booking unavailable.` :
    regularSlotsAvailable ? `${slots.slots_left} regular slot${slots.slots_left !== 1 ? "s" : ""} available.` :
    overrideSlotsAvailable ? `Regular slots are full. ${slots.override_slots_left} override slot${slots.override_slots_left !== 1 ? "s" : ""} available.` :
    "No regular or override slots available.";

  const openDetailsFromSearch = async () => {
    if (!phone.trim()) { toast.error("Enter mobile number first."); return; }
    const data = patientContext || await loadPatientContext(phone);
    if (!data) { toast.error("Could not search patient."); return; }
    if (data?.patient?.name) setName(data.patient.name);
    else if (data?.saved_contact?.name) setName(data.saved_contact.name);
    setShowDetails(true);
  };

  return open ? (
    !showDetails ? (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4 py-6" onClick={onClose}>
        <div className="w-full max-w-lg rounded-2xl border border-border bg-card shadow-xl" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-start justify-between gap-4 border-b border-border bg-background/80 px-6 py-5">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Find patient</div>
              <h2 className="font-display font-bold text-2xl text-foreground mt-1">Enter mobile number</h2>
              <p className="text-sm text-muted-foreground mt-1">Search existing patients before creating a walk-in booking.</p>
            </div>
            <button type="button" onClick={onClose} className="rounded-full p-2 hover:bg-muted transition-colors">
              <X size={18} weight="bold" />
            </button>
          </div>

          <div className="p-6">
            <Label className="text-sm font-semibold text-foreground">Mobile number</Label>
            <div className="relative mt-2">
              <Phone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" weight="duotone" />
              <Input
                type="tel"
                value={phone}
                onChange={(e) => handlePhoneChange(e.target.value)}
                placeholder="e.g. 9876543210"
                className="h-13 rounded-xl pl-11 text-base"
                autoFocus
              />
            </div>
            {checkingPhone && <p className="mt-2 text-sm text-muted-foreground">Searching patient…</p>}

            {patientContext && (
              <div className="mt-4 rounded-2xl border border-border bg-background p-4">
                {existingPatient ? (
                  <button type="button" onClick={openDetailsFromSearch} className="flex w-full items-center gap-3 text-left">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground font-black">
                      {(existingPatient.name || "P").charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-black text-foreground truncate">{existingPatient.name}</div>
                      <div className="text-sm text-muted-foreground truncate">{existingPatient.phone || existingPatient.mobile_number || phone}</div>
                    </div>
                    <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary">Select</span>
                  </button>
                ) : (
                  <div>
                    <div className="font-black text-foreground">
                      {patientContext.saved_contact?.name ? patientContext.saved_contact.name : "No registered patient found"}
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {patientContext.saved_contact?.name
                        ? "Saved walk-in contact found. Continue to book without creating an app account."
                        : "Continue as a new walk-in. When this patient registers later with this number, appointments will link to their account."}
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="mt-6 grid grid-cols-2 gap-3">
              <Button type="button" variant="outline" onClick={onClose} className="h-11 rounded-xl">Cancel</Button>
              <Button type="button" onClick={openDetailsFromSearch} disabled={checkingPhone || phone.trim().length < 4} className="h-11 rounded-xl bg-primary text-primary-foreground hover:bg-[#1a3025]">
                {existingPatient ? "Continue" : "Continue new"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    ) : (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4 py-6" onClick={onClose}>
      <div className="w-full max-w-5xl max-h-[92vh] overflow-hidden rounded-2xl bg-card border border-border shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-border bg-background/80 px-6 py-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Reception booking</div>
              <h2 className="font-display font-bold text-2xl text-foreground mt-1">Add patient to queue</h2>
              <p className="text-sm text-muted-foreground mt-1">Search by phone, select who the visit is for, then confirm the appointment.</p>
            </div>
            <button type="button" onClick={onClose} className="rounded-full p-2 hover:bg-muted transition-colors">
              <X size={18} weight="bold" />
            </button>
          </div>

        </div>

        <div className="max-h-[calc(92vh-112px)] overflow-y-auto px-6 py-6 space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <section>
              <SectionTitle icon={<Users size={19} weight="duotone" />} title="Patient Profile" />
              <div className="rounded-lg border border-border bg-background/70 p-4">
                <DetailRow label="Full Name" value={selectedDependent?.name || existingPatient?.name || name || "-"} />
                <DetailRow label="Profile Type" value={selectedDependent ? selectedDependent.relationship : existingPatient ? "Registered patient" : "New walk-in"} />
                <DetailRow label="Contact" value={selectedDependent?.phone || existingPatient?.phone || existingPatient?.mobile_number || phone || "-"} last />
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2">
                <InfoPill label="Family" value={`${patientContext?.dependents?.length || 0}`} icon={<Users size={14} weight="duotone" />} />
                <InfoPill label="History" value={`${patientContext?.history_count || 0}`} icon={<ClockCounterClockwise size={14} weight="duotone" />} />
                <InfoPill label="Active" value={`${activeWarnings.length}`} tone={activeWarnings.length ? "warn" : "default"} icon={<WarningCircle size={14} weight="duotone" />} />
              </div>
              {!existingPatient && (
                <div className="mt-3">
                  <Label className="text-sm font-semibold text-foreground">Patient name *</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Enter patient name" className="h-11 rounded-lg mt-1" />
                </div>
              )}
            </section>

            <section>
              <div className="flex items-center justify-between gap-3">
                <SectionTitle icon={<Users size={19} weight="duotone" />} title="Family Members" />
                {existingPatient && (
                  <button type="button" onClick={() => setFamilyFormOpen(true)} className="inline-flex items-center gap-1 rounded-full border border-primary/20 bg-primary/10 px-3 py-1.5 text-xs font-bold text-primary hover:bg-primary/15">
                    <UserPlus size={13} /> Add family
                  </button>
                )}
              </div>
              <div className="rounded-lg border border-border bg-background/70 p-4">
                <Label className="block text-sm font-semibold text-muted-foreground mb-2">Book appointment for</Label>
                <select
                  value={selectedProfileId}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedProfileId(value);
                    if (value === "self") {
                      if (existingPatient?.name) setName(existingPatient.name);
                      return;
                    }
                    const dep = (patientContext?.dependents || []).find((d) => d.id === value);
                    if (dep?.name) setName(dep.name);
                  }}
                  className="w-full h-11 rounded-lg border border-border bg-card px-3 text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="self">{existingPatient?.name || "Self / walk-in"} — {existingPatient ? "Registered patient" : "Temporary walk-in"}</option>
                  {(patientContext?.dependents || []).map((dep) => (
                    <option key={dep.id} value={dep.id}>
                      {dep.name} — {dep.relationship}{dep.phone ? ` · ${dep.phone}` : ""}
                    </option>
                  ))}
                </select>
                {existingPatient && (patientContext?.dependents || []).length === 0 && (
                  <p className="mt-2 text-sm text-muted-foreground">No family members linked yet.</p>
                )}
              </div>
            </section>
          </div>

          {activeWarnings.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-center gap-2 text-sm font-black text-amber-800">
                <WarningCircle size={17} weight="duotone" /> Active booking found
              </div>
              <div className="mt-2 space-y-1">
                {activeWarnings.map((a) => (
                  <div key={a.id} className="text-xs text-amber-800">
                    {a.patient_name} · Dr {a.doctor_name} · {a.queue_date} · #{String(a.token_number).padStart(2, "0")} · {a.status}
                  </div>
                ))}
              </div>
            </div>
          )}

          {(selectedFollowUp || recentCompletedForProfile.length > 0) && (
            <div className={`rounded-lg border p-4 ${
              selectedFollowUp?.eligible
                ? "border-primary/20 bg-primary/10 text-primary"
                : "border-border bg-background/70 text-muted-foreground"
            }`}>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <div className="text-sm font-black text-foreground">
                    {selectedFollowUp?.eligible ? "Follow-up eligible" : "Previous visit found"}
                  </div>
                  <p className="mt-1 text-sm">
                    {selectedFollowUp?.eligible
                      ? selectedFollowUp.message
                      : selectedFollowUp?.reason || "This profile has completed visit history."}
                  </p>
                </div>
                {selectedFollowUp?.eligible && (
                  <div className="rounded-full bg-card px-3 py-1 text-xs font-black text-primary shadow-sm">
                    Follow-up fee ₹{selectedFollowUp.follow_up_fee ?? 0}
                  </div>
                )}
              </div>
              {recentCompletedForProfile.length > 0 && (
                <div className="mt-3 space-y-1">
                  {recentCompletedForProfile.slice(0, 2).map((a) => (
                    <div key={a.id} className="text-xs">
                      Last completed: {a.patient_name} · {a.queue_date} · Token #{String(a.token_number || "").padStart(2, "0")}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <section className="rounded-lg border border-border bg-background/70 p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <SectionTitle icon={<CalendarBlank size={19} weight="duotone" />} title="Schedule Appointment" />
              <Button
                type="button"
                onClick={submit}
                disabled={!canSubmit}
                className="h-10 rounded-full bg-primary px-5 text-primary-foreground hover:bg-[#1a3025]"
              >
                {busy ? "Adding…" : "Add patient"}
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-5">
              {isStaff && staffDoctors.length > 1 && (
                <div>
                  <Label className="block text-sm font-semibold text-muted-foreground mb-2">Doctor</Label>
                  <select value={selectedDoctorId} onChange={(e) => { setSelectedDoctorId(e.target.value); setSelectedLocationId(""); setSelectedSessionId(""); setSlots(null); }} className="w-full h-11 rounded-lg border border-border bg-card px-3 text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary">
                    <option value="">Select doctor…</option>
                    {staffDoctors.map((d) => <option key={d.id} value={d.id}>{d.name}{d.specialty ? ` — ${d.specialty}` : ""}</option>)}
                  </select>
                </div>
              )}
              {locations.length > 1 && (
                <div>
                  <Label className="block text-sm font-semibold text-muted-foreground mb-2">Location</Label>
                  <select value={selectedLocationId} onChange={(e) => handleLocationChange(e.target.value)} className="w-full h-11 rounded-lg border border-border bg-card px-3 text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary">
                    <option value="">Select location…</option>
                    {locations.map((loc) => <option key={loc.id} value={loc.id}>{loc.clinic_name}</option>)}
                  </select>
                </div>
              )}
              <div>
                <Label className="block text-sm font-semibold text-muted-foreground mb-2">Select Date</Label>
                <DateTimeField
                  mode="date"
                  value={date}
                  onChange={setDate}
                  minDate={new Date().toISOString().slice(0, 10)}
                  placeholder="Choose date"
                />
              </div>
              <div>
                <Label className="block text-sm font-semibold text-muted-foreground mb-2">Reason</Label>
                <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Optional" className="h-11 rounded-lg" />
              </div>
              <div>
                <Label className="block text-sm font-semibold text-muted-foreground mb-2">Booking Type</Label>
                <select value={bookingType} onChange={(e) => setBookingType(e.target.value)} className="w-full h-11 rounded-lg border border-border bg-card px-3 text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary">
                  <option value="new">New Booking</option>
                  <option value="follow_up" disabled={!selectedFollowUp?.eligible}>Follow-up{selectedFollowUp?.eligible ? ` · ₹${selectedFollowUp.follow_up_fee ?? 0}` : ""}</option>
                  {showOverrideOption && <option value="doctor_override">Doctor override</option>}
                </select>
                {!selectedFollowUp?.eligible && selectedFollowUp?.reason && (
                  <p className="mt-1 text-xs text-muted-foreground">{selectedFollowUp.reason}</p>
                )}
              </div>
              {bookingType === "doctor_override" && (
                <div>
                  <Label className="block text-sm font-semibold text-muted-foreground mb-2">Override Reason</Label>
                  <Input value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} placeholder="Doctor approval reason" className="h-11 rounded-lg" />
                </div>
              )}
              <div>
                <Label className="block text-sm font-semibold text-muted-foreground mb-2">Session</Label>
                <select value={selectedSessionId} onChange={(e) => setSelectedSessionId(e.target.value)} className="w-full h-11 rounded-lg border border-border bg-card px-3 text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary">
                  <option value="">Choose Session</option>
                  {(slots?.sessions || []).map((s) => (
                    <option key={s.id} value={s.id} disabled={s.is_past}>
                      {s.label || formatTime12(s.start_time?.slice(0, 5))} · {s.slots_left ?? 0} left
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className={`mt-5 rounded-lg border p-3 text-sm font-semibold ${
              slotLoading
                ? "border-border bg-card text-muted-foreground"
                : slots && (!slots.clinic_open || slots.is_blocked_date || !slots.is_working_day || (!regularSlotsAvailable && !overrideSlotsAvailable))
                  ? "border-destructive/20 bg-destructive/5 text-destructive"
                  : slots && regularSlotsAvailable
                    ? "border-primary/20 bg-primary/10 text-primary"
                    : slots ? "border-orange-200 bg-orange-50 text-orange-700" : "border-border bg-card text-muted-foreground"
            }`}>
              {slotLoading ? "Checking availability…" : slots ? slotMessage : "Select a date to view availability."}
            </div>
          </section>
        </div>

      </div>
      <AddFamilyMemberModal
        open={familyFormOpen}
        busy={familyBusy}
        form={familyForm}
        onChange={setFamilyForm}
        onClose={() => {
          setFamilyFormOpen(false);
          setFamilyForm({ name: "", relationship: "Other", phone: "" });
        }}
        onSubmit={addFamilyMember}
      />
    </div>
  )) : null;
}

function InfoPill({ label, value, icon, tone = "default" }) {
  return (
    <div className={`rounded-xl border px-3 py-2 ${tone === "warn" ? "border-amber-200 bg-amber-50 text-amber-800" : "border-border bg-background text-foreground"}`}>
      <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
        {icon} {label}
      </div>
      <div className="mt-1 text-lg font-black">{value}</div>
    </div>
  );
}

function SectionTitle({ icon, title }) {
  return (
    <h3 className="mb-4 flex items-center gap-2 text-base font-bold text-foreground">
      <span className="text-primary">{icon}</span>
      {title}
    </h3>
  );
}

function DetailRow({ label, value, last = false }) {
  return (
    <div className={`flex items-center justify-between gap-4 py-2 ${last ? "" : "border-b border-border"}`}>
      <span className="text-sm font-semibold text-muted-foreground">{label}</span>
      <span className="min-w-0 truncate text-right text-sm font-bold text-foreground">{value}</span>
    </div>
  );
}

function AddFamilyMemberModal({ open, busy, form, onChange, onClose, onSubmit }) {
  if (!open) return null;
  const set = (key, value) => onChange((prev) => ({ ...prev, [key]: value }));
  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/45 px-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary mb-3">
              <UserPlus size={20} weight="duotone" />
            </div>
            <h3 className="font-display text-xl font-bold text-foreground">Add family member</h3>
            <p className="mt-1 text-sm text-muted-foreground">Save the dependent once, then book visits for them from this patient profile.</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground">
            <X size={18} weight="bold" />
          </button>
        </div>

        <div className="mt-5 space-y-4">
          <div>
            <Label className="text-sm font-semibold text-foreground">Full name *</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Ayaan's father" className="mt-1 h-11 rounded-xl" autoFocus />
          </div>
          <div>
            <Label className="text-sm font-semibold text-foreground">Relationship *</Label>
            <select value={form.relationship} onChange={(e) => set("relationship", e.target.value)} className="mt-1 h-11 w-full rounded-xl border border-border bg-background px-3 text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary">
              {["Spouse", "Child", "Parent", "Sibling", "Grandparent", "Other"].map((rel) => <option key={rel} value={rel}>{rel}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-sm font-semibold text-foreground">Phone number</Label>
            <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Optional, leave blank to use main patient phone" className="mt-1 h-11 rounded-xl" />
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3">
          <Button type="button" variant="outline" onClick={onClose} className="h-11 rounded-xl">Cancel</Button>
          <Button type="button" onClick={onSubmit} disabled={busy} className="h-11 rounded-xl bg-primary text-primary-foreground hover:bg-[#1a3025]">
            {busy ? "Adding…" : "Add member"}
          </Button>
        </div>
      </div>
    </div>
  );
}
